import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { statusMeta } from '../orders/orderConstants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: 'blocked', dot: '🔴', label: 'Dossiers bloqués' },
  { key: 'lab_questions', dot: '🟠', label: 'Questions laboratoire en attente' },
  { key: 'certificates_missing', dot: '🟡', label: 'Certificats manquants' },
  { key: 'supplier_invoices_missing', dot: '🟢', label: 'Factures fournisseur non déposées' },
  { key: 'client_invoices_to_validate', dot: '🔵', label: 'Factures client à valider' },
  { key: 'shipments_today', dot: '🚚', label: 'Expéditions du jour' },
  { key: 'registrations_pending', dot: '📝', label: 'Inscriptions en attente' },
  { key: 'late_orders', dot: '⏰', label: 'Commandes en retard' },
];

const SectionCard = ({ dot, label, data, invoiceMode, shipmentMode, registrationMode }) => (
  <Card className={`border-border ${data.count > 0 ? '' : 'opacity-70'}`} data-testid={`cockpit-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center justify-between">
        <span>{dot} {label}</span>
        <span className={`text-2xl font-bold ${data.count > 0 ? '' : 'text-muted-foreground'}`}>{data.count}</span>
      </CardTitle>
    </CardHeader>
    {data.items?.length > 0 && (
      <CardContent className="pt-0 space-y-1">
        {data.items.slice(0, 5).map((it) => invoiceMode ? (
          <Link key={it.id} to="/admin/invoicing" className="flex items-center justify-between text-xs hover:underline">
            <span className="font-mono">{it.invoice_number}</span>
            <span className="text-muted-foreground">{it.cabinet_name} · {it.total?.toFixed(2)} €</span>
          </Link>
        ) : shipmentMode ? (
          <Link key={it.id} to={`/admin/orders/${it.order_id}`} className="flex items-center justify-between text-xs hover:underline">
            <span className="font-mono">{it.order_number}</span>
            <span className="text-muted-foreground">{it.carrier_label}{it.tracking_number ? ` · ${it.tracking_number}` : ''}</span>
          </Link>
        ) : registrationMode ? (
          <Link key={it.id} to="/admin/registrations" className="flex items-center justify-between text-xs hover:underline">
            <span className="font-medium truncate">{it.cabinet_name}</span>
            <span className="text-muted-foreground">{it.structure}</span>
          </Link>
        ) : (
          <Link key={it.id} to={`/admin/orders/${it.id}`} className="flex items-center justify-between text-xs hover:underline">
            <span className="font-mono">{it.order_number}</span>
            <Badge variant="outline" className={`${statusMeta(it.status).cls} text-[10px]`}>{statusMeta(it.status).label}</Badge>
          </Link>
        ))}
      </CardContent>
    )}
  </Card>
);

export default function AdminCockpit() {
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cockpit`);
      setData(data);
    } catch (e) {
      toast.error('Erreur de chargement du cockpit');
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 30000);
    return () => clearInterval(t);
  }, [fetchData]);

  return (
    <AdminLayout title="Cockpit" subtitle="Où agir maintenant — mise à jour toutes les 30 s">
      {!data ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {SECTIONS.map((s) => (
              <SectionCard key={s.key} dot={s.dot} label={s.label} data={data[s.key] || { count: 0, items: [] }}
                invoiceMode={s.key === 'client_invoices_to_validate'}
                shipmentMode={s.key === 'shipments_today'}
                registrationMode={s.key === 'registrations_pending'} />
            ))}
            <Card className="border-border" data-testid="cockpit-production-par-labo">
              <CardHeader className="pb-2"><CardTitle className="text-sm">📈 En production par laboratoire</CardTitle></CardHeader>
              <CardContent className="pt-0 space-y-1">
                {data.in_production_by_lab.length === 0 && <p className="text-xs text-muted-foreground">Aucun dossier en production.</p>}
                {data.in_production_by_lab.map((r) => (
                  <div key={r.lab} className="flex items-center justify-between text-xs">
                    <span>{r.lab}</span><span className="font-bold">{r.count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
